// Service Worker cleanup and resource optimization utilities
// Note: Service worker registration has been removed - the app requires
// internet connectivity, and proper HTTP caching headers provide better
// cache behavior without the complexity of SW lifecycle management.

import { logger } from './logger';

/**
 * Unregister all service workers and clear their caches.
 * This cleans up existing SW installations from users who had them registered
 * from previous deployments.
 */
export const unregisterAllServiceWorkers = async (): Promise<void> => {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  try {
    // Get all service worker registrations
    const registrations = await navigator.serviceWorker.getRegistrations();

    if (registrations.length > 0) {
      logger.info(`[SW Cleanup] Found ${registrations.length} service worker(s) to unregister`);

      // Unregister all service workers
      await Promise.all(
        registrations.map(async (registration) => {
          const success = await registration.unregister();
          if (success) {
            logger.info('[SW Cleanup] Service worker unregistered successfully');
          }
        }),
      );
    }

    // Clear all caches created by the old service worker
    const cacheNames = await caches.keys();
    if (cacheNames.length > 0) {
      logger.info(`[SW Cleanup] Clearing ${cacheNames.length} cache(s)`);
      await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));
    }
  } catch (error) {
    logger.warn('[SW Cleanup] Error during service worker cleanup:', { error });
  }
};

// Check if app is running offline
export const isOffline = (): boolean => {
  return !navigator.onLine;
};

// Network status utilities
export const addNetworkStatusListeners = (
  onOnline: () => void,
  onOffline: () => void,
): (() => void) => {
  const handleOnline = () => {
    onOnline();
  };

  const handleOffline = () => {
    onOffline();
  };

  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);

  // Return cleanup function
  return () => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
  };
};

// Resource hints for better performance
export const addResourceHints = (): void => {
  // DNS prefetch for external domains
  const dnsPrefetchDomains = ['https://fonts.googleapis.com', 'https://fonts.gstatic.com'];

  dnsPrefetchDomains.forEach((domain) => {
    const link = document.createElement('link');
    link.rel = 'dns-prefetch';
    link.href = domain;
    document.head.appendChild(link);
  });

  // Preconnect to critical origins
  const preconnectOrigins = ['https://fonts.googleapis.com', 'https://fonts.gstatic.com'];

  preconnectOrigins.forEach((origin) => {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = origin;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  });
};
